import type { FC } from "react";
import { twMerge } from "tailwind-merge";

import { PageAccordionSection } from "./PageAccordionSection";
import { SectionTitle } from "./SectionTitle";
import { TopicSection } from "./TopicSection";

import { t } from "i18n/i18n";
import { LongFormPencilIcon, PaperClipLinkIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionKind,
  selectPageIdsForCurrentSection,
  selectVisibleTopicsWithPages,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

export interface ManageSectionPagesContentProps {
  className?: string;
  scrollAreaClassName?: string;
}

export const ManageSectionPagesContent: FC<ManageSectionPagesContentProps> = ({
  className,
  scrollAreaClassName,
}) => {
  const topicsWithPages = useListSelector(selectVisibleTopicsWithPages);
  const assignmentPageIds = useListSelector((s) => selectPageIdsForCurrentSection(s, "assignment"));
  const resourcePageIds = useListSelector((s) =>
    selectPageIdsForCurrentSection(s, "course_resource"),
  );
  const sectionKind = useAppSelector(selectCurrentSectionKind);

  const visibleTopicsWithPages = topicsWithPages.filter((topic) => topic.pages.length > 0);

  const hasAnyContent =
    assignmentPageIds.length > 0 || visibleTopicsWithPages.length > 0 || resourcePageIds.length > 0;

  return (
    <div className={twMerge("w-full @container/page-metadata", className)}>
      <div className="mb-3 flex items-center gap-3 text-sm text-black-tint-40">
        {t("manage_section_pages_modal.subheader.title", {
          assignments: assignmentPageIds.length,
          topics: visibleTopicsWithPages.length,
          resources: resourcePageIds.length,
          pageCount: visibleTopicsWithPages.reduce((sum, topic) => sum + topic.pages.length, 0),
        })}
      </div>

      <div className={twMerge("overflow-y-auto pr-1", scrollAreaClassName)}>
        {!hasAnyContent ? (
          <div className="py-8 text-center text-black-tint-55">
            {t("manage_section_pages_modal.empty")}
          </div>
        ) : (
          <>
            {assignmentPageIds.length > 0 && (
              <PageAccordionSection
                pageIds={assignmentPageIds}
                sectionKind={sectionKind}
                title={
                  <SectionTitle
                    count={assignmentPageIds.length}
                    icon={<LongFormPencilIcon />}
                    title={t("page_variant_plural.assignment")}
                  />
                }
              />
            )}

            {visibleTopicsWithPages.map((topic) => (
              <TopicSection key={topic.id} sectionKind={sectionKind} topic={topic} />
            ))}

            {resourcePageIds.length > 0 && (
              <PageAccordionSection
                pageIds={resourcePageIds}
                sectionKind={sectionKind}
                title={
                  <SectionTitle
                    count={resourcePageIds.length}
                    icon={<PaperClipLinkIcon />}
                    title={t("page_variant_plural.course_resource")}
                  />
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
